import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { AttributeBoolean } from '@recruitee/common';
import {
  Attachment,
  MailboxMessageAttachmentAssignment,
  VisibilityLevel,
} from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';

import { WindowRef } from '../../../common/window-ref';
import { DialogService } from '../../../dialogs/dialogs.module';
import { IconName } from '../../../icons-container/icons.type';
import {
  AttachmentPreviewDialogComponent,
  AttachmentPreviewDialogData,
} from '../attachment-preview-dialog/attachment-preview-dialog.component';
import { AttachmentType } from './attachment-item.types';

interface AttachmentAction {
  label: string;
  icon: IconName;
  variant?: string;
  handler: () => void;
}

@Component({
  selector: 'rt-attachment-item',
  templateUrl: './attachment-item.component.html',
  styleUrls: ['./attachment-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.attachment-list]': 'type === "list-item"',
  },
})
export class AttachmentItemComponent {
  public readonly CV_COMPATIBLE_EXTENSIONS: string[] = [
    'doc',
    'docx',
    'pdf',
    'odt',
    'rtf',
    'txt',
    'png',
    'jpg',
    'html',
    'htm',
  ];

  @Input() public type: AttachmentType = 'card';
  @Input() public attachment: Attachment;
  @Input() public attachmentAssignment: MailboxMessageAttachmentAssignment;
  @Input() @AttributeBoolean() public hasSetAsCvAction: boolean = false;
  @Input() @AttributeBoolean() public hasDeleteAction: boolean = false;
  @Input() @AttributeBoolean() public hasDownloadAction: boolean = true;
  @Input() @AttributeBoolean() public highlightedFilename: string | null = null;

  @Input() public moreTemplate: TemplateRef<any> | null;
  @Input() public footerIcon?: IconName;
  @Input() public translations: {
    setAsCv: string;
    delete: string;
    previewDialog: AttachmentPreviewDialogData['translations'];
  } = {
    setAsCv: 'rtu.attachment.set_as_cv',
    delete: 'rtu.common.delete',
    previewDialog: {
      closeDialogTooltip: 'rtu.dialog.close_tooltip',
      downloadFile: 'rtu.attachment.download_file',
    },
  };

  @Output() public setAsCv: EventEmitter<Attachment> = new EventEmitter();
  @Output() public deleteClick: EventEmitter<Attachment> = new EventEmitter();

  public additionalOptsDropdownOpen: boolean = false;
  public actions: AttachmentAction[] = [];
  public href: SafeUrl;

  private thumbnailBroken: boolean = false;

  constructor(
    public sanitizer: DomSanitizer,
    private dialogService: DialogService,
    private viewContainerRef: ViewContainerRef,
    private windowRef: WindowRef,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  public get isPrivate(): boolean {
    if (!this.attachment || !this.attachment.visibility) {
      return false;
    }

    return this.attachment.visibility.level !== VisibilityLevel.Public;
  }

  public ngOnChanges(): void {
    const actions: AttachmentAction[] = [];

    if (
      this.hasSetAsCvAction &&
      this.CV_COMPATIBLE_EXTENSIONS.includes(this.attachment.extension)
    ) {
      actions.push({
        label: this.i18n.translate(this.translations.setAsCv),
        icon: 'file-account',
        variant: 'primary-on-hover',
        handler: () => {
          this.setAsCv.emit(this.attachment);
          this.toggleAdditionalOptsDropdown();
        },
      });
    }

    if (this.hasDeleteAction) {
      actions.push({
        label: this.i18n.translate(this.translations.delete),
        icon: 'delete',
        handler: () => this.delete(),
        variant: 'danger-on-hover',
      });
    }

    this.actions = actions;
    this.setHref();
  }

  public thumbnailLoadError(): void {
    this.thumbnailBroken = true;
    this.cdRef.detectChanges();
  }

  public trackByLabel(index: number, action: AttachmentAction): string {
    return action.label;
  }

  public toggleAdditionalOptsDropdown(
    value: boolean = !this.additionalOptsDropdownOpen,
    evt?: MouseEvent,
  ): void {
    if (evt) evt.preventDefault();

    this.additionalOptsDropdownOpen = value;
    this.cdRef.detectChanges();
  }

  public hasThumbnail(): boolean {
    return this.thumbnailUrl() !== null && !this.thumbnailBroken;
  }

  public thumbnailUrl(): string | null {
    return this.attachment.imageThumbnailUrl || this.attachment.pdfThumbnailUrl || null;
  }

  public isDocumentOrImage(): boolean {
    return !!this.attachment?.pdfUrl || !!this.attachment?.imageUrl;
  }

  public hasUploader(): boolean {
    return this.attachment.admin || this.attachment.candidate || this.attachment.guest;
  }

  public uploaderPhotoUrl(): string | undefined {
    if (this.attachment.uploader === 'Admin' && this.attachment.admin) {
      return this.attachment.admin.photoThumbUrl;
    }
    if (this.attachment.uploader === 'Candidate' && this.attachment.candidate) {
      return this.attachment.candidate.photoThumbUrl;
    }
    if (this.attachment.uploader === 'Guest' && this.attachment.guest) {
      return this.attachment.guest.photoThumbUrl;
    }

    return;
  }

  public uploaderName(): string | void {
    if (this.attachment.uploader === 'Admin' && this.attachment.admin) {
      return (
        this.attachment.admin.name ||
        `${this.attachment.admin.firstName} ${this.attachment.admin.lastName}`
      );
    }
    if (this.attachment.uploader === 'Candidate' && this.attachment.candidate) {
      return this.attachment.candidate.name;
    }
    if (this.attachment.uploader === 'Guest' && this.attachment.guest) {
      return this.attachment.guest.name;
    }
  }

  public open(): void {
    if (this.isDocumentOrImage()) {
      this.dialogService.custom(
        AttachmentPreviewDialogComponent,
        { width: '900px', closeTooltip: this.translations.previewDialog.closeDialogTooltip },
        <AttachmentPreviewDialogData>{
          attachment: this.attachment,
          translations: this.translations.previewDialog,
        },
        this.viewContainerRef,
      );
    } else if (this.attachment.fileUrl) {
      this.windowRef.nativeWindow.open(this.attachment.fileUrl);
    }
  }

  public delete(): void {
    this.deleteClick.emit(this.attachment);
  }

  private setHref(): void {
    // eslint-disable-next-line ban/ban
    this.href = this.sanitizer.bypassSecurityTrustUrl(this.attachment.fileUrl);
  }
}
